import { useMemo } from "react";

import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { getActiveViewListQueryDocument } from "./active-view.gql";

export interface UseGetActiveViewListProps {
  workspaceId?: string;
  enabled?: boolean;
}

export const useGetActiveViewList = ({ workspaceId, enabled = true }: UseGetActiveViewListProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.activeViews.list(workspaceId ?? ""),
    queryFn: async () => {
      if (!workspaceId) {
        throw new Error("Workspace ID is required");
      }
      const client = createOperationClient("GetActiveViewList");
      const data = await client.request(getActiveViewListQueryDocument, {
        input: { workspaceId },
      });
      return data.activeViewList;
    },
    enabled: enabled && !!workspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes for individual items
  });

  const activeViewList = useMemo(() => {
    return queryResult.data ?? null;
  }, [queryResult.data]);

  return {
    activeViewList,
    loading: queryResult.isLoading,
    error: queryResult.error,
    queryResult,
  };
};
